import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ActiveTimer, TimeLog } from '@shared/types';
import { apiFetch } from '@/lib/api';
import { useApiToken } from './useApiToken';

const ACTIVE_KEY = ['timer', 'active'] as const;

// The active-timer query polls every 30s on top of refetch-on-focus, so
// a timer started on another tab/device shows up here within half a minute.
// The header pill ticks locally every second between fetches.
export function useActiveTimer() {
  const { data: tokenData, isSuccess: tokenReady } = useApiToken();
  return useQuery({
    queryKey: ACTIVE_KEY,
    queryFn: () => apiFetch<ActiveTimer | null>('/timer/active', { token: tokenData?.token }),
    enabled: tokenReady,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useStartTimer() {
  const qc = useQueryClient();
  const { data: tokenData } = useApiToken();
  return useMutation({
    mutationFn: (taskId: string) =>
      apiFetch<TimeLog>(`/timer/start/${taskId}`, {
        method: 'POST',
        token: tokenData?.token,
      }),
    // Optimistic: paint the active-timer cache immediately so the header
    // pill appears on the next render instead of waiting for /timer/active
    // to refetch. The real payload (name, section, estimate) lands when
    // the invalidate completes.
    onMutate: async (taskId) => {
      await qc.cancelQueries({ queryKey: ACTIVE_KEY });
      const previous = qc.getQueryData<ActiveTimer | null>(ACTIVE_KEY);
      qc.setQueryData<ActiveTimer | null>(ACTIVE_KEY, {
        timeLogId: 'optimistic',
        taskId,
        taskName: '…',
        sectionId: '',
        startedAt: new Date().toISOString(),
        timeEstimateMinutes: null,
      });
      return { previous };
    },
    onError: (_err, _taskId, ctx) => {
      if (ctx) qc.setQueryData(ACTIVE_KEY, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ACTIVE_KEY }),
  });
}

export function useStopTimer() {
  const qc = useQueryClient();
  const { data: tokenData } = useApiToken();
  return useMutation({
    mutationFn: () =>
      apiFetch<TimeLog | null>('/timer/stop', {
        method: 'POST',
        token: tokenData?.token,
      }),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ACTIVE_KEY });
      const previous = qc.getQueryData<ActiveTimer | null>(ACTIVE_KEY);
      qc.setQueryData<ActiveTimer | null>(ACTIVE_KEY, null);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) qc.setQueryData(ACTIVE_KEY, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ACTIVE_KEY }),
  });
}
